import { GrainGradient } from "@paper-design/shaders-react";
import { useEffect, useRef, useState } from "react";

// Cold palette (default)
const COLD_COLORS = ["#7300ff", "#eba8ff", "#00bfff", "#2a00ff"];
// Warm palette (on hover / movement)
const WARM_COLORS = ["#ff4d00", "#ffb199", "#ffd166", "#ff006e"];

// Resting blob position (matches the shader's originX / originY)
const REST_ORIGIN_X = 1;
const REST_ORIGIN_Y = 0.65;
// How close (in normalized units) the cursor must be to the blob center to count as "over the blob"
const HOVER_RADIUS = 0.32;
// Threshold above which we start spawning fragment blobs
const SPLIT_VELOCITY = 0.35;
// Minimum ms between spawns
const SPAWN_COOLDOWN_MS = 60;
// Fragment lifetime
const FRAGMENT_LIFE_MS = 1600;

// Spring physics — lower damping = more bounce/elastic
const SPRING_STIFFNESS = 220;
const SPRING_DAMPING = 11;

interface Fragment {
  id: number;
  // start position (in vw / vh units, 0..100)
  x: number;
  y: number;
  // velocity (vw or vh per second)
  vx: number;
  vy: number;
  // size in px
  size: number;
  // color hex
  color: string;
  // spawn time
  born: number;
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

function hexToRgb(hex: string) {
  const h = hex.replace("#", "");
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
}

function rgbToHex(r: number, g: number, b: number) {
  const toHex = (v: number) =>
    Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function mixColors(coldHex: string, warmHex: string, t: number) {
  const c = hexToRgb(coldHex);
  const w = hexToRgb(warmHex);
  return rgbToHex(
    lerp(c.r, w.r, t),
    lerp(c.g, w.g, t),
    lerp(c.b, w.b, t)
  );
}

export function Shader() {
  const containerRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const lastMoveTimeRef = useRef<number>(0);
  const lastPosRef = useRef<{ x: number; y: number }>({ x: 0.5, y: 0.5 });
  const lastSpawnTimeRef = useRef<number>(0);
  const fragmentIdRef = useRef<number>(0);
  const fragmentsRef = useRef<Fragment[]>([]);
  const cursorRef = useRef<{ x: number; y: number }>({ x: 0.5, y: 0.5 });

  const targetRef = useRef({
    originX: REST_ORIGIN_X,
    originY: REST_ORIGIN_Y,
    speed: 1,
    warmth: 0,
    softness: 0.77,
    intensity: 0.39,
  });
  // Spring state for elastic interpolation (position + velocity per param)
  const springRef = useRef({
    speed: { pos: 1, vel: 0 },
    warmth: { pos: 0, vel: 0 },
    softness: { pos: 0.77, vel: 0 },
    intensity: { pos: 0.39, vel: 0 },
  });
  const [state, setState] = useState({
    originX: REST_ORIGIN_X,
    originY: REST_ORIGIN_Y,
    speed: 1,
    warmth: 0,
    softness: 0.77,
    intensity: 0.39,
  });
  const [fragments, setFragments] = useState<Fragment[]>([]);

  useEffect(() => {
    const spawnFragments = (
      originNx: number,
      originNy: number,
      cursorNx: number,
      cursorNy: number,
      velocity: number,
      warmth: number
    ) => {
      const count = Math.min(4, Math.max(1, Math.floor(velocity * 5)));
      const now = performance.now();
      const newFragments: Fragment[] = [];
      for (let i = 0; i < count; i += 1) {
        // Direction: away from cursor (same as blob push direction) with jitter
        const angleToBlob = Math.atan2(
          originNy - cursorNy,
          originNx - cursorNx
        );
        const angle = angleToBlob + (Math.random() - 0.5) * Math.PI * 0.9;
        const speed = 15 + Math.random() * 45 + velocity * 60; // vw/vh per second
        const size = 60 + Math.random() * 140 + velocity * 80;
        // Pick a color, blend between cold and warm palette based on warmth
        const idx = Math.floor(Math.random() * COLD_COLORS.length);
        const color = mixColors(
          COLD_COLORS[idx],
          WARM_COLORS[idx],
          Math.min(1, warmth + Math.random() * 0.3)
        );
        newFragments.push({
          id: fragmentIdRef.current++,
          x: originNx * 100,
          y: originNy * 100,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          size,
          color,
          born: now,
        });
      }
      fragmentsRef.current = [...fragmentsRef.current, ...newFragments];
    };

    const handleMove = (e: MouseEvent) => {
      const now = performance.now();
      const dt = now - lastMoveTimeRef.current;

      const nx = e.clientX / window.innerWidth;
      const ny = e.clientY / window.innerHeight;
      cursorRef.current = { x: nx, y: ny };

      const bx = targetRef.current.originX;
      const by = targetRef.current.originY;
      const dxToBlob = bx - nx;
      const dyToBlob = by - ny;
      const distFromBlob = Math.sqrt(
        dxToBlob * dxToBlob + dyToBlob * dyToBlob
      );
      const withinBlobRadius = distFromBlob < HOVER_RADIUS;

      // Also treat hovering over any element with data-shader-hover as "over the blob"
      const target = e.target as HTMLElement | null;
      const overHoverEl = !!target?.closest?.("[data-shader-hover]");

      const isOverBlob = withinBlobRadius || overHoverEl;

      if (!isOverBlob) {
        lastPosRef.current = { x: nx, y: ny };
        return;
      }

      lastMoveTimeRef.current = now;

      // Velocity magnitude for extra reactivity
      const dx = nx - lastPosRef.current.x;
      const dy = ny - lastPosRef.current.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const velocity = dt > 0 ? Math.min(1, (dist / (dt / 1000)) * 0.5) : 0;
      lastPosRef.current = { x: nx, y: ny };

      // Blob stays put — no positional push. Only visual reactions below.
      // When hovering the text (not close to blob), keep a strong baseline closeness so the effect is still obvious.
      const radialCloseness = withinBlobRadius
        ? 1 - distFromBlob / HOVER_RADIUS
        : 0;
      const closeness = Math.max(radialCloseness, overHoverEl ? 0.75 : 0);

      // Speed up + warm up + split — cranked way up for a more obvious kick
      targetRef.current.speed = 3 + velocity * 10 + closeness * 6;
      targetRef.current.warmth = Math.min(
        1,
        closeness * 0.9 + velocity * 0.5
      );
      targetRef.current.intensity =
        0.39 + closeness * 0.5 + velocity * 0.3;
      targetRef.current.softness = Math.max(
        0.18,
        0.77 - closeness * 0.6 - velocity * 0.25
      );

      // Spawn fragments when moving aggressively over the blob (only when actually near the blob)
      if (
        withinBlobRadius &&
        velocity > SPLIT_VELOCITY &&
        now - lastSpawnTimeRef.current > SPAWN_COOLDOWN_MS
      ) {
        lastSpawnTimeRef.current = now;
        spawnFragments(
          targetRef.current.originX,
          targetRef.current.originY,
          nx,
          ny,
          velocity,
          targetRef.current.warmth
        );
      }
    };

    const handleLeave = () => {
      targetRef.current.speed = 1;
      targetRef.current.warmth = 0;
      targetRef.current.intensity = 0.39;
      targetRef.current.softness = 0.77;
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseleave", handleLeave);

    // Main animation loop: spring-interpolate state + update fragments
    let lastTick = performance.now();

    const stepSpring = (
      spring: { pos: number; vel: number },
      target: number,
      deltaSec: number
    ) => {
      const force = (target - spring.pos) * SPRING_STIFFNESS;
      spring.vel += (force - spring.vel * SPRING_DAMPING) * deltaSec;
      spring.pos += spring.vel * deltaSec;
    };

    const tick = () => {
      const now = performance.now();
      const deltaSec = Math.min(0.05, (now - lastTick) / 1000);
      lastTick = now;

      // Update fragments physics
      const alive: Fragment[] = [];
      for (const f of fragmentsRef.current) {
        if (now - f.born < FRAGMENT_LIFE_MS) {
          f.x += f.vx * deltaSec;
          f.y += f.vy * deltaSec;
          f.vx *= 0.985;
          f.vy *= 0.985;
          alive.push(f);
        }
      }
      fragmentsRef.current = alive;
      setFragments([...alive]);

      // Decay targets back to rest when cursor has been idle
      const target = targetRef.current;
      const timeSinceMove = now - lastMoveTimeRef.current;
      if (timeSinceMove > 120) {
        target.speed = lerp(target.speed, 1, 0.06);
        target.warmth = lerp(target.warmth, 0, 0.06);
        target.intensity = lerp(target.intensity, 0.39, 0.06);
        target.softness = lerp(target.softness, 0.77, 0.06);
      }

      // Spring-step each shader param toward its target for elastic bounce
      const s = springRef.current;
      stepSpring(s.speed, target.speed, deltaSec);
      stepSpring(s.warmth, target.warmth, deltaSec);
      stepSpring(s.intensity, target.intensity, deltaSec);
      stepSpring(s.softness, target.softness, deltaSec);

      setState({
        originX: REST_ORIGIN_X,
        originY: REST_ORIGIN_Y,
        speed: Math.max(0.2, s.speed.pos),
        warmth: Math.max(0, Math.min(1, s.warmth.pos)),
        intensity: Math.max(0, s.intensity.pos),
        softness: Math.max(0.1, s.softness.pos),
      });

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseleave", handleLeave);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const blendedColors = COLD_COLORS.map((c, i) =>
    mixColors(c, WARM_COLORS[i], state.warmth)
  );

  return (
    <div
      ref={containerRef}
      style={{ position: "fixed", inset: 0, zIndex: -1, overflow: "hidden" }}
    >
      <GrainGradient
        fit={"contain"}
        scale={1}
        rotation={0}
        offsetX={0}
        offsetY={0}
        speed={state.speed}
        originX={state.originX}
        originY={state.originY}
        worldWidth={0}
        worldHeight={0}
        colorBack={"#000000"}
        colors={blendedColors}
        softness={state.softness}
        intensity={state.intensity}
        noise={0.49}
        shape={"blob"}
        style={{ width: "100%", height: "100%" }}
      />

      {/* Fragment blobs spawned on aggressive hover */}
      {fragments.map((f) => {
        const age = (performance.now() - f.born) / FRAGMENT_LIFE_MS;
        const opacity = Math.max(0, 1 - age) * 0.85;
        const scale = 0.6 + age * 0.9;
        return (
          <div
            key={f.id}
            style={{
              position: "absolute",
              left: `${f.x}vw`,
              top: `${f.y}vh`,
              width: `${f.size}px`,
              height: `${f.size}px`,
              transform: `translate(-50%, -50%) scale(${scale})`,
              background: `radial-gradient(circle, ${f.color} 0%, ${f.color}00 70%)`,
              opacity,
              pointerEvents: "none",
              filter: "blur(12px)",
              mixBlendMode: "screen",
            }}
          />
        );
      })}
    </div>
  );
}

export default Shader;